import React, { useEffect, useMemo, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { toAbsoluteUrl } from '../AssetHelpers';

type Props = {
	className?: string;
	path: string;
	svgClassName?: string;
	height?: string;
	width?: string;
};

// Global cache for SVG content (stores the actual SVG markup)
// This persists across component mounts/unmounts
const svgContentCache = new Map<string, string>();

// Preload an SVG and cache it
const preloadSvg = (url: string): Promise<string> => {
	return new Promise((resolve, reject) => {
		if (svgContentCache.has(url)) {
			resolve(svgContentCache.get(url)!);
			return;
		}

		fetch(url)
			.then(response => {
				if (!response.ok) {
					throw new Error(`Failed to fetch SVG: ${response.status}`);
				}
				return response.text();
			})
			.then(svgContent => {
				svgContentCache.set(url, svgContent);
				resolve(svgContent);
			})
			.catch(reject);
	});
};

const KTSVG: React.FC<Props> = ({
	className = '',
	path,
	svgClassName = 'mh-50px',
	height,
	width,
}) => {
	
	if (!path) {
		return null;
	}
	
	// Strip timestamp query params for consistent caching
	const cleanPath = useMemo(() => path.replace(/\?timestamp=\d+/, ''), [path]);

	// Memoize the absolute URL
	const absoluteUrl = useMemo(() => toAbsoluteUrl(cleanPath), [cleanPath]);

	// State for inline SVG content
	const [svgContent, setSvgContent] = useState<string | null>(
		svgContentCache.get(absoluteUrl) || null
	);
	const [isLoading, setIsLoading] = useState(!svgContent);
	const [hasError, setHasError] = useState(false);

	// Preload SVG on mount if not already cached
	useEffect(() => {
		let isMounted = true;

		if (!svgContentCache.has(absoluteUrl)) {
			setIsLoading(true);
			preloadSvg(absoluteUrl)
				.then(content => {
					if (isMounted) {
						setSvgContent(content);
						setIsLoading(false);
					}
				})
				.catch(() => {
					if (isMounted) {
						setHasError(true);
						setIsLoading(false);
					}
				});
		} else {
			// Already in cache, use cached content
			setSvgContent(svgContentCache.get(absoluteUrl)!);
			setIsLoading(false);
		}

		return () => {
			isMounted = false;
		};
	}, [absoluteUrl]);

	// Add svgClassName to the SVG element - MUST be before any conditional returns
	const processedSvgContent = useMemo(() => {
		if (!svgContent) return '';
		// Add the svgClassName to the SVG element
		return svgContent.replace(/<svg/, `<svg class="${svgClassName}"`);
	}, [svgContent, svgClassName]);

	// Show skeleton while loading
	if (isLoading) {
		return (
			<span
				className={`svg-icon ${className}`}
				style={{ height: height, width: width }}
			>
				<SkeletonTheme
					borderRadius='6px'
					baseColor='var(--kt-gray-300)'
					highlightColor='var(--kt-light)'
				>
					<Skeleton height={height} width={width} inline={true} />
				</SkeletonTheme>
			</span>
		);
	}

	// Show nothing on error (or could show an error icon)
	if (hasError || !svgContent) {
		return (
			<span
				className={`svg-icon ${className}`}
				style={{ height: height, width: width }}
			/>
		);
	}

	// Render the cached SVG content directly
	return (
		<span
			className={`svg-icon ${className}`}
			style={{ height: height, width: width }}
			dangerouslySetInnerHTML={{ __html: processedSvgContent }}
		/>
	);
};

export { KTSVG };
